import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, ChevronDown, Pencil, Trash2, Check } from 'lucide-react';
import toast from 'react-hot-toast';

// Same look/behavior as ReferenceDropdown.tsx (search box + custom list +
// inline "add new" affordance), but generic over {id, name} options instead
// of being wired to the reference-options store — so it can sit on top of
// any backend list (ledger categories/subcategories here).
export interface SearchableSelectOption {
  id: number | string;   // usually a numeric row id; may be a name string for name-keyed lists (e.g. managers)
  name: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;                                                  // selected option's id, as a string ('' if none)
  onChange: (id: string) => void;
  onAddNew: (name: string) => Promise<SearchableSelectOption | null>;
  placeholder?: string;
  addNewLabel?: string;                                           // e.g. "category" / "subcategory" — shown in the "Add ... permanently" row
  // Optional per-option management (opt-in): when provided, each option row
  // gets an inline edit (rename) and delete affordance. onRenameOption returns
  // true on success so the row can exit edit mode. onDeleteOption hands control
  // to the parent (which typically confirms impact before actually deleting).
  onRenameOption?: (option: SearchableSelectOption, newName: string) => Promise<boolean>;
  onDeleteOption?: (option: SearchableSelectOption) => void;
  // 'inline' strips the component's own chrome — bordered box, clear/chevron
  // icons — so it can sit in a dense table cell and read as plain text until
  // hovered, instead of turning the table into a wall of form controls. The
  // input's appearance then comes entirely from `inputClassName`, which an
  // inline caller is expected to supply (e.g. the staff table's `.icell`);
  // nothing is set inline, so that class's :hover/:focus rules still win.
  variant?: 'default' | 'inline';
  inputClassName?: string;
  // Adds a "—" row that clears the selection. Worth having wherever the field
  // is optional, since typing can select or create but never un-set.
  allowClear?: boolean;
  title?: string;
  // Greys the input and stops the menu opening. For a dependent dropdown whose
  // parent hasn't been picked yet: the list is empty for a reason the "type to
  // add one" empty state would otherwise misexplain (the add would be rejected).
  disabled?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  onAddNew,
  placeholder = 'Select...',
  addNewLabel = 'item',
  onRenameOption,
  onDeleteOption,
  variant = 'default',
  inputClassName,
  allowClear = false,
  title,
  disabled = false,
}) => {
  const inline = variant === 'inline';
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Option currently being renamed inline (its id) + the working text.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [renamingBusy, setRenamingBusy] = useState(false);
  const manageable = !!(onRenameOption || onDeleteOption);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, maxWidth: 0 });
  // Comfortable minimum for the portaled list so names stay readable even when
  // the trigger input sits in a narrow table column.
  const DROPDOWN_MIN_WIDTH = 240;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(
    () => options.find(o => String(o.id) === String(value))?.name || '',
    [options, value]
  );

  useEffect(() => { setSearchTerm(selectedLabel); }, [selectedLabel]);

  // Leaving the dropdown abandons any half-finished inline rename.
  useEffect(() => { if (!isOpen) { setEditingId(null); setEditingName(''); setHoveredId(null); } }, [isOpen]);

  // The dropdown is portaled to <body> (see render below) so it can float
  // above any scrollable ancestor — e.g. the batch-entry modal's table area —
  // instead of being clipped by that ancestor's overflow:auto. Since it's no
  // longer a DOM descendant of containerRef, position is computed from the
  // input's viewport rect and kept in sync on scroll/resize while open.
  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      // maxWidth caps how far the list may grow to the right before it would run
      // off-screen; beyond that, long option names wrap to the next line.
      if (rect) setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width, maxWidth: window.innerWidth - rect.left - 8 });
    };
    updatePosition();
    // capture:true catches scroll on any nested scrollable container, not just window
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = !!containerRef.current?.contains(target);
      const insideDropdown = !!dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false);
        setSearchTerm(selectedLabel); // revert to actual selection if nothing was picked
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedLabel]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(opt => opt.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, options]);

  const exactMatchExists = options.some(
    opt => opt.name.toLowerCase().trim() === searchTerm.toLowerCase().trim()
  );

  // Create + select in one go. Shared by the footer row and the Enter key so
  // both land on exactly the same behaviour.
  const commitNew = async (name: string) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const created = await onAddNew(name);
      if (created) {
        onChange(String(created.id));
        setIsOpen(false);
      }
    } catch {
      toast.error(`Failed to add ${addNewLabel}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNewRowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Nothing typed yet — just point the user at the search box instead of
    // silently doing nothing, so the "add new" affordance is always visible
    // rather than only appearing once they've started typing.
    if (!searchTerm.trim()) {
      inputRef.current?.focus();
      return;
    }
    commitNew(searchTerm.trim());
  };

  // Enter commits whatever is typed without reaching for the mouse: a name
  // already on the list selects it (so Enter can never mint a duplicate),
  // anything else is added — the same thing the footer row does.
  //
  // Escape closes the dropdown and stops there, rather than also reaching the
  // screen behind it and closing whatever editor the dropdown is sitting in.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (!isOpen) return;
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setSearchTerm(selectedLabel);   // revert to the actual selection
      return;
    }
    if (e.key !== 'Enter') return;
    // Never let it submit a surrounding form or trip a parent's Enter handler.
    e.preventDefault();
    e.stopPropagation();
    const typed = searchTerm.trim();
    if (!typed) { setIsOpen(false); return; }
    const match = options.find(opt => opt.name.toLowerCase() === typed.toLowerCase());
    if (match) {
      onChange(String(match.id));
      setIsOpen(false);
      return;
    }
    commitNew(typed);
  };

  const beginRename = (opt: SearchableSelectOption) => {
    setEditingId(String(opt.id));
    setEditingName(opt.name);
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const commitRename = async (opt: SearchableSelectOption) => {
    if (!onRenameOption) return;
    const next = editingName.trim();
    if (!next || next === opt.name) { cancelRename(); return; }
    setRenamingBusy(true);
    try {
      const ok = await onRenameOption(opt, next);
      if (ok) cancelRename();
    } finally {
      setRenamingBusy(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          ref={inputRef}
          className={inputClassName}
          title={title}
          // Inline callers style the input through inputClassName — setting
          // anything here would beat that class's :hover/:focus rules.
          style={inline ? { width: '100%', boxSizing: 'border-box', ...(disabled ? { color: '#94a3b8' } : {}) } : {
            padding: '0.35rem 1.8rem 0.35rem 0.5rem',
            borderRadius: '6px',
            border: `1px solid ${isOpen ? '#3b82f6' : '#cbd5e1'}`,
            fontSize: '0.875rem',
            outline: 'none',
            backgroundColor: disabled ? '#f8fafc' : '#fff',
            color: disabled ? '#94a3b8' : undefined,
            width: '100%',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
          placeholder={placeholder}
          value={searchTerm}
          disabled={disabled}
          onFocus={() => { if (!disabled) setIsOpen(true); }}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onKeyDown={handleKeyDown}
        />
        {!inline && !disabled && (
          <div style={{ position: 'absolute', right: '6px', display: 'flex', gap: '2px', color: '#94a3b8' }}>
            {searchTerm && (
              <X size={14} style={{ cursor: 'pointer' }} onClick={() => { onChange(''); setSearchTerm(''); }} />
            )}
            <ChevronDown size={14} style={{ cursor: 'pointer', transform: isOpen ? 'rotate(180deg)' : 'none' }} onClick={() => setIsOpen(!isOpen)} />
          </div>
        )}
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          // Marks this as dropdown chrome that lives outside its owner in the
          // DOM. Screens with a click-outside handler (e.g. the Ledger
          // Workspace's close-the-edit-row behaviour) check for this so picking
          // an option isn't mistaken for a click away from the editor.
          data-portal-dropdown="true"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            // As wide as the content needs (so names aren't truncated), but at
            // least the trigger width / a readable minimum, and never past the
            // screen edge — where names wrap instead.
            width: 'max-content',
            minWidth: Math.max(coords.width, DROPDOWN_MIN_WIDTH),
            maxWidth: Math.max(coords.maxWidth, DROPDOWN_MIN_WIDTH),
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '260px',
          }}
        >
          {/* Scrollable option list — capped height so the "Add new" row below
              stays pinned and visible without having to scroll past everything. */}
          <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, maxHeight: '210px' }}>
            {/* Only while nothing is typed — once the box is a search term, a
                "clear" row sitting above the matches is just noise. */}
            {allowClear && !searchTerm && (
              <div
                onMouseDown={() => { onChange(''); setIsOpen(false); }}
                style={{
                  padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer',
                  borderBottom: '1px solid #f1f5f9', color: '#64748b', fontStyle: 'italic',
                }}
              >
                — none —
              </div>
            )}
            {filteredOptions.map(opt => {
              const optId = String(opt.id);
              const isSelected = String(value) === optId;
              const isEditing = editingId === optId;
              const showActions = manageable && (hoveredId === optId || isEditing);
              return (
                <div
                  key={opt.id}
                  onMouseEnter={() => setHoveredId(optId)}
                  onMouseLeave={() => setHoveredId(prev => (prev === optId ? null : prev))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 10px 8px 12px',
                    fontSize: '0.8rem',
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: isEditing ? '#fffbeb' : isSelected ? '#eff6ff' : (hoveredId === optId ? '#f8fafc' : 'transparent'),
                    color: isSelected ? '#1d4ed8' : '#1e293b',
                  }}
                >
                  {isEditing ? (
                    <>
                      {/* Inline rename — fix a typo without leaving the dropdown. */}
                      <input
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(opt); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                        style={{ flex: 1, minWidth: 0, padding: '3px 6px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '0.8rem', outline: 'none' }}
                      />
                      <Check
                        size={15}
                        style={{ cursor: renamingBusy ? 'wait' : 'pointer', color: '#16a34a', flexShrink: 0 }}
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); commitRename(opt); }}
                      />
                      <X
                        size={15}
                        style={{ cursor: 'pointer', color: '#94a3b8', flexShrink: 0 }}
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); cancelRename(); }}
                      />
                    </>
                  ) : (
                    <>
                      <span
                        onMouseDown={() => { onChange(optId); setIsOpen(false); }}
                        style={{ flex: 1, minWidth: 0, cursor: 'pointer', whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                      >
                        {opt.name}
                      </span>
                      {onRenameOption && (
                        <span
                          title={`Rename ${addNewLabel}`}
                          style={{ display: 'inline-flex', flexShrink: 0, visibility: showActions ? 'visible' : 'hidden' }}
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); beginRename(opt); }}
                        >
                          <Pencil size={14} style={{ cursor: 'pointer', color: '#64748b' }} />
                        </span>
                      )}
                      {onDeleteOption && (
                        <span
                          title={`Delete ${addNewLabel}`}
                          style={{ display: 'inline-flex', flexShrink: 0, visibility: showActions ? 'visible' : 'hidden' }}
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(false); onDeleteOption(opt); }}
                        >
                          <Trash2 size={14} style={{ cursor: 'pointer', color: '#ef4444' }} />
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {filteredOptions.length === 0 && !searchTerm && (
              <div style={{ padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
                No options yet. Type below to add one.
              </div>
            )}
          </div>

          {/* Pinned footer — always visible, never scrolls out of view. */}
          {!exactMatchExists && (
            <div
              onMouseDown={handleAddNewRowClick}
              style={{
                padding: '10px 12px',
                backgroundColor: '#eff6ff',
                cursor: isSaving ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#2563eb',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderTop: '1px solid #e2e8f0',
                borderRadius: '0 0 8px 8px',
                flex: '0 0 auto',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={14} />
                <span>{searchTerm.trim() ? `Add "${searchTerm.trim()}" ${addNewLabel}` : `Add new ${addNewLabel}`}</span>
              </div>
              {/* Says what Enter will do, so the shortcut is found rather than
                  guessed at. */}
              {searchTerm.trim() && (
                <span style={{
                  flexShrink: 0, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.02em',
                  color: '#1d4ed8', background: '#dbeafe', border: '1px solid #bfdbfe',
                  borderRadius: '4px', padding: '1px 5px', whiteSpace: 'nowrap',
                }}>
                  ↵ Enter
                </span>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
